import { TimePicker } from "@/components/ui/datetime";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { MdClear } from "react-icons/md";
import { parseImportTemporal } from "@/utils/importTemporal";

interface TimeOnlyProps {
    value: string | number | { seconds: number; nanoseconds?: number } | null;
    onChange?: (date: Date | null) => void;
}

export const TimeOnly = ({ value, onChange }: TimeOnlyProps) => {
    const [time, setTime] = useState<Date | null>(null);
    const [isParsed, setIsParsed] = useState(false);
    const expectedTimestampRef = useRef<number | null>(null);

    const handleOnChange = (date: Date | undefined) => {
        if (!date) {
            console.log("Invalid time");
            setIsParsed(false);
            return;
        }
        setTime(date);
        setIsParsed(true);
        onChange?.(date);
    };

    const handleClearTime = () => {
        expectedTimestampRef.current = null;
        setTime(null);
        setIsParsed(false);
        onChange?.(null);
    };

    const handleSetNow = () => {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000);
        expectedTimestampRef.current = timestamp;
        console.log('[TimeOnly] Setting now:', now, 'timestamp:', timestamp);
        setTime(now);
        setIsParsed(true);
        onChange?.(now);
    };

    useEffect(() => {
        try {
            if (value === null || value === 0) {
                if (expectedTimestampRef.current === null) {
                    setIsParsed(false);
                    setTime(null);
                }
                return;
            }
            
            let parsedDate: Date | null = null;

            if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
                parsedDate = new Date(value.seconds * 1000);
            } else {
                parsedDate = parseImportTemporal(value, {
                    timeOnly: true,
                    absoluteIsoDateTime: true,
                });
            }
            
            if (parsedDate && !isNaN(parsedDate.getTime())) {
                const propTimestamp = Math.floor(parsedDate.getTime() / 1000);

                if (expectedTimestampRef.current !== null && expectedTimestampRef.current === propTimestamp) {
                    expectedTimestampRef.current = null;
                    return;
                }

                if (expectedTimestampRef.current !== null) {
                    return;
                }

                setIsParsed(true);
                setTime(parsedDate);
            } else {
                throw new Error("Invalid Date");
            }
        } catch (error) {
            console.error('[TimeOnly] Error parsing time value:', value, error);
            if (expectedTimestampRef.current === null) {
                setIsParsed(false);
                setTime(null);
            }
        }
    }, [value]);

    return (
        <div className="flex items-center" style={{ zIndex: 99999 }}>
            {isParsed ? (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <TimePicker
                            date={time ?? undefined}
                            onChange={handleOnChange}
                            granularity="minute"
                            hourCycle={24}
                        />
                    </div>
                    <Button
                        onClick={handleClearTime}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        <MdClear />
                    </Button>
                </div>
            ) : (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <TimePicker
                            date={undefined}
                            onChange={handleOnChange}
                            granularity="minute"
                            hourCycle={24}
                        />
                    </div>
                    <Button
                        onClick={handleSetNow}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        Now
                    </Button>
                </div>
            )}
        </div>
    );
};

